import { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { LoginData, LoginError } from 'auth/models';
import { GENERIC_MESSAGES } from 'shared/constants/GenericMessages';
import { useAuthService } from 'auth/services/useAuthService';
import { useImpersonationService } from 'auth/services/useImpersonationService';
import { useUserService } from 'user/services/useUserService';
import { Usuario } from 'user/models/Usuario';
import { LoginPhase } from 'navigation/models/login-phase';
import { useSecurity } from 'auth/hooks/useSecurity';
import { LOGIN_ERROR_MESSAGES } from 'auth/constants/LoginErrorMessages';
import { useRootLoadingSpinner } from 'shared/spinner';
import { useSingleFetch } from 'shared/http';
import { useNotifications } from 'shared/notifications';
import {
  enrichAuthenticatedUser,
  isImpersonationActor,
  resolvePostLoginPath,
} from 'auth/impersonationRules';
import {
  CONFIRMATION_EMAIL_PATH,
  IMPERSONATION_HOME_PATH,
  PASSWORD_EXPIRED_PATH,
  RECOVER_PASSWORD_PATH,
  RESET_PASSWORD_PATH,
} from '../../user/routes';
import { useLocation, useNavigate } from 'react-router-dom';

interface AuthContextType {
  disableSignIn: boolean;
  user: Usuario;
  login: (username: string, password: string) => void;
  loginAsUser: (username: string) => void;
  exitImpersonation: () => void;
  logout: () => void;
  isLogged: () => boolean;
  updateUserData: () => void;
}

const LOADING_USER = { funcionalidades: ['CARGANDO'] } as Usuario;

const contextInitialValue = {
  user: null,
  disableSignIn: false,
  login: async () => null,
  logout: () => {},
  loginAsUser: async () => null,
  exitImpersonation: async () => null,
  isLogged: () => false,
  updateUserData: () => null,
};

export const AuthContext = createContext<AuthContextType>(contextInitialValue);

const determineLoginPhase = (user: Usuario): LoginPhase => {
  if (!user.mail || user.mail.trim() === '') {
    return LoginPhase.EMAIL_REQUIRED_BLOCKED;
  }

  const phase = {
    confirmed: LoginPhase.LOGGED_IN,
    unconfirmed: LoginPhase.LOGGED_IN,
  };
  if (!user.mailStatus) return LoginPhase.LOGGED_IN;
  return phase[user.mailStatus] ?? LoginPhase.LOGGED_IN;
};

export const AuthProvider = ({ children }) => {
  const { accessToken, saveTokens, removeTokens, saveLoginPhase } = useSecurity();
  const [user, setUser] = useState<Usuario>(accessToken ? LOADING_USER : null);
  const { setRootLoadingSpinnerStatus } = useRootLoadingSpinner();
  const [disableSignIn, setDisableSignIn] = useState<boolean>(false);
  const navigate = useNavigate();
  const authService = useAuthService();
  const impersonationService = useImpersonationService();
  const userService = useUserService();
  const { addNotification } = useNotifications();
  const location = useLocation();
  const singleFetch = useSingleFetch();
  const isPasswordExpired = useRef(false);
  const isLoginAction = useRef(false);
  const isExitImpersonationAction = useRef(false);

  const handleAuthError = useCallback(
    (error: unknown, fallbackMessage: string) => {
      const msg = error instanceof LoginError ? error.message : fallbackMessage;
      addNotification(msg, 'error');
    },
    [addNotification],
  );

  const fetchAuthenticatedUser = useCallback(() => {
    return userService
      .getAuthenticatedUserData()
      .then((response) => {
        setUser(enrichAuthenticatedUser(response));
        if (isExitImpersonationAction.current) {
          const loginPhase = determineLoginPhase(response);
          saveLoginPhase(loginPhase);
          navigate(IMPERSONATION_HOME_PATH);
          isExitImpersonationAction.current = false;
          return;
        }
        if (isLoginAction.current && !isPasswordExpired.current) {
          const loginPhase = determineLoginPhase(response);
          saveLoginPhase(loginPhase);
          const postLoginPath = resolvePostLoginPath(response?.tipoUsuario, loginPhase);
          if (postLoginPath) {
            navigate(postLoginPath);
          }
          isLoginAction.current = false;
        }
      })
      .catch(() => {
        isExitImpersonationAction.current = false;
        addNotification(GENERIC_MESSAGES.authenticationError, 'error');
        setUser(null);
      });
  }, [saveLoginPhase, userService, addNotification, navigate]);

  const updateUser = useCallback(
    (options?: { force?: boolean }) => {
      const run = () => fetchAuthenticatedUser();
      if (options?.force) {
        run();
      } else {
        singleFetch(run);
      }
    },
    [fetchAuthenticatedUser, singleFetch],
  );

  const isPublicPath = (pathname: string) => {
    const publicPaths = [
      CONFIRMATION_EMAIL_PATH.split('/:')[0],
      RECOVER_PASSWORD_PATH,
      RESET_PASSWORD_PATH.split('/:')[0],
    ];
    return publicPaths.some((path) => pathname.startsWith(path));
  };

  useEffect(() => {
    if (isPublicPath(location.pathname)) {
      return;
    }
    if (accessToken) {
      updateUser();
    } else {
      setUser(null);
    }
  }, [updateUser, accessToken, location.pathname]);

  const handleUserLogin = (userData: LoginData) => {
    isLoginAction.current = true;
    saveTokens(userData);
    if (userData?.credentialsExpired) {
      isPasswordExpired.current = true;
      saveLoginPhase(LoginPhase.EXPIRED_PASSWORD);
      navigate(PASSWORD_EXPIRED_PATH);
    } else {
      isPasswordExpired.current = false;
    }
  };

  const performLogin = (username: string, password: string) => {
    setDisableSignIn(true);
    setRootLoadingSpinnerStatus(true);
    authService
      .doLogin(username, password)
      .then((response) => {
        handleUserLogin(response.body);
      })
      .catch((e) => {
        const msg = e instanceof LoginError ? e.message : GENERIC_MESSAGES.genericError;
        removeTokens();
        addNotification(msg, 'error');
      })
      .finally(() => {
        setRootLoadingSpinnerStatus(false);
        setDisableSignIn(false);
      });
  };

  const contextValue: AuthContextType = {
    disableSignIn,
    user,
    login: performLogin,
    loginAsUser(username) {
      if (user == null || !isImpersonationActor(user.tipoUsuario)) {
        addNotification(LOGIN_ERROR_MESSAGES.Unauthorized, 'error');
        return;
      }
      setRootLoadingSpinnerStatus(true);
      impersonationService
        .loginAsUser(username)
        .then((response) => handleUserLogin(response.body))
        .catch(() =>
          addNotification(LOGIN_ERROR_MESSAGES.impersonationDenied, 'error'),
        )
        .finally(() => setRootLoadingSpinnerStatus(false));
    },
    exitImpersonation() {
      isExitImpersonationAction.current = true;
      setUser(LOADING_USER);
      setRootLoadingSpinnerStatus(true);
      impersonationService
        .exitImpersonation()
        .then((response) => {
          saveTokens(response.body);
          updateUser({ force: true });
        })
        .catch((e) => {
          isExitImpersonationAction.current = false;
          handleAuthError(e, GENERIC_MESSAGES.genericError);
        })
        .finally(() => setRootLoadingSpinnerStatus(false));
    },
    logout: useCallback(() => {
      authService.doLogout().catch((e) => {});
      removeTokens();
      saveLoginPhase(LoginPhase.LOGIN);
    }, [authService, removeTokens, saveLoginPhase]),
    isLogged: useCallback(() => {
      return Boolean(user);
    }, [user]),
    updateUserData: useCallback(() => {
      setTimeout(() => {
        if (accessToken) {
          updateUser();
        }
      });
    }, [updateUser, accessToken]),
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
